

// Probably need an ID gotten off the route, useParams mayhaps?

import { Box, inputBaseClasses, MenuItem, Paper, styled, TextField, useTheme } from "@mui/material";
import { gridClasses, GridColDef, GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { GetFieldLookup, GetFieldLookupItem, ILookup, useFormMetadata, useFormSave, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { FormDefinitions, ProjectCommission } from "../api/dtos";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import ProjectContext from "../contexts/data/ProjectContext";
import useProjectCommissionsByProjectId from "../hooks/data/useProjectCommissionsByProjectId";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

interface GridLookupParams extends GridRenderEditCellParams {
    lookups?: ILookup[]
}

const DataGridSelect:FC<GridLookupParams> = (props) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = +event.target.value; // The new value entered by the user
        apiRef.current.setEditCellValue({ id: props.id, field: props.field, value: newValue });
    };

    return (
        <TextField
            select
            value={props.value}
            fullWidth
            variant="standard"
            slotProps={{
                input: {
                    disableUnderline: true
                }
            }}
            sx={{
                alignSelf: 'center',
                flex: '1 1 auto',
                justifyContent: 'stretch',
            }}
            onChange={handleValueChange}
        >
            {GetFieldLookup(props.lookups, `${props.field.charAt(0).toUpperCase()}${props.field.substring(1)}`)?.items?.map((x, i) => (
                <MenuItem 
                    key={i.toString()} 
                    value={x.id?.toString() ?? ""}
                    sx={{
                        paddingLeft: theme.spacing(1)
                    }}
                >
                    {x.value?.toString()}
                </MenuItem>
            ))}
        </TextField>
    );
}

const DataGridDatePicker:FC<GridRenderEditCellParams> = (params) => {
    const theme = useTheme();

    const apiRef = useGridApiContext();

    const handleValueChange = (value: any) => {
        const newValue = value as DateTime; // The new value entered by the user
        apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: newValue?.toISO({includeOffset: false})});
    };

    return (
        <DatePicker
            format="dd/MM/yyyy"
            slotProps={{
                textField: {
                    fullWidth: true,
                    variant: "standard",
                    sx: {
                        alignSelf: 'center',
                        flex: '1 1 auto',
                        justifyContent: 'stretch',
                        border: "none",
                        margin: theme.spacing(0, 1),
                        [`& .${inputBaseClasses.root}::before`]: {
                            display: 'none'
                        },
                        [`& .${inputBaseClasses.root}::after`]: {
                            display: 'none'
                        }
                    },
                    slotProps: {
                        input: {
                            disableUnderline: true
                        },
                    }
                }
            }}
            value={params?.value ? DateTime.fromISO(params.value) : null}
            onChange={(value, _context) => handleValueChange(value as any)}
        />
    );
}

const useProjectCommissionColumns = () => {

    const metadata = useFormMetadata(FormDefinitions.ProjectCommission);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectCommissionColumns = useMemo(() => {
        const columns: GridColDef<ProjectCommission>[] =  [
            {
                field: "commission",
                headerName: "Commission",
                editable: true,
                flex: 1
            },
            {
                field: "dateEffective",
                headerName: "Date Effective",
                valueFormatter: (_value, row) => {
                    return row.dateEffective ? DateTime.fromISO(row.dateEffective).toFormat("dd/MM/yyyy") : undefined;
                },
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridDatePicker
                        {...params?? {}}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            },
            {
                field: "commissionType",
                headerName: "Commission Type",
                renderCell: (params) => GetFieldLookupItem(lookups, 'CommissionType', params.row.commissionType)?.value?.toString(),
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridSelect
                        {...params?? {}}
                        lookups={lookups ?? []}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            },
            {
                field: "commissionValueType",
                headerName: "Commission Paid As",
                renderCell: (params) => GetFieldLookupItem(lookups, 'CommissionValueType', params.row.commissionValueType)?.value?.toString(),
                editable: true,
                renderEditCell: (params: GridRenderEditCellParams) => (
                    <DataGridSelect
                        {...params?? {}}
                        lookups={lookups ?? []}
                        value={params.value ? params.value : ""} 
                    />
                ),
                flex: 1
            }
        ];

        return columns;
    }, [lookups]);

    return projectCommissionColumns;
}

export const BulkEditProjectCommissions: FC = () => {
    const projectCommissionMetadata= useFormMetadata(FormDefinitions.ProjectCommission);

    const save = useFormSave(projectCommissionMetadata);

    const theme = useTheme();
    
    const {data: _lotLookups, loading: _lookupsLoading} = useLookups(projectCommissionMetadata);
    
    const params = useParams();
    
    const projectId = Number(params.projectId);
        
    const {data: projectCommissions, loading: projectCommissionsLoading, refresh: projectCommissionsRefresh} = useProjectCommissionsByProjectId(projectId);

    const submit = (newProjectCommission: ProjectCommission) => {
        save(newProjectCommission);
        return newProjectCommission;
    }
    
    const addNewProjectCommission = async () => {
        let lot = new ProjectCommission(
            {
                projectId: projectId
            }
        );
        await save(lot);
        await projectCommissionsRefresh();
        return lot;
    }

    const {data: project} = useContext(ProjectContext);
    
    
    const customToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - Commissions`,
        fileName: `${project?.name} - Commissions`,
        includeDateTime: true
    }), [project]);    
    
    const columns = useProjectCommissionColumns();

    return (

        //Properties

        //Summary

        //StageList
        <>
            <Outlet />

            <TitleBox
                display={"flex"}
                justifyContent={"flex-end"}
            >
                <StyledButton 
                    variant="contained"
                    onClick={addNewProjectCommission}
                >
                    Add Commission
                </StyledButton>
            </TitleBox>

            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
                            
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                                
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                                    
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                                
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                },
                                
                                '&.column-group-header': {
                                    backgroundColor: theme.palette.grey[300],
                                    boxShadow: "1",
                                    textAlign: 'center'
                                }
                            }
                        },
                    }}
                    >
                    <CollapsibleTablePremium
                        density="compact"
                        editMode="row"
                        processRowUpdate={(newRow, _oldRow, _params) => submit(newRow)}
                        //onRowEditStop={(params, event) => lotEditSubmit(params, event)}
                        startCollapsed={false} 
                        title={"Project Commissions"} 
                        loading={projectCommissionsLoading} 
                        columns={columns}
                        rows={projectCommissions}
                        disableColumnMenu={false}
                        disableColumnFilter={false}
                        {...customToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </>
    );
}